import { useGetPublishedPostsQuery } from "../../../../../app/websiteApiSlice";
import { useGetPublicSchoolInfoQuery } from "../../../../../app/authApiSlice";
import WebsiteHeader from "../../WebsiteHeader";
import WebsiteFooter from "../../WebsiteFooter";
import SeoHead from "../../SeoHead";
import "./modern.css";
import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia,
  CardActionArea, Chip, Button, CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";

export default function ModernTheme({ settings }) {
  const { data: school } = useGetPublicSchoolInfoQuery();
  const { data: postsData, isLoading } = useGetPublishedPostsQuery({ per_page: 6 });

  return (
    <Box className="website-modern" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <SeoHead
        title={settings?.tagline || school?.name}
        description={settings?.tagline}
        schoolName={school?.name}
      />
      <WebsiteHeader school={school} navItems={settings?.nav_items} variant="modern" />

      {/* Hero */}
      <Box
        sx={{
          py: { xs: 8, md: 14 },
          background: "linear-gradient(135deg, #f5f7ff 0%, #e8f4fd 100%)",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: "2rem", md: "3.5rem" } }}>
            {school?.name || "Welcome"}
          </Typography>
          {settings?.tagline && (
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              {settings.tagline}
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/blog"
            sx={{ borderRadius: 8, px: 4 }}
          >
            Read Our Blog
          </Button>
        </Container>
      </Box>

      {/* Recent posts */}
      <Container maxWidth="lg" sx={{ py: 8, flex: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight={700}>Latest Posts</Typography>
          <Button component={Link} to="/blog">All Posts →</Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            {(postsData?.posts || []).map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", borderRadius: 3, display: "flex", flexDirection: "column", "&:hover": { boxShadow: 3 } }}
                >
                  <CardActionArea component={Link} to={`/blog/${post.slug}`} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                    {post.featured_image && (
                      <CardMedia component="img" height="180" image={post.featured_image} alt={post.title} />
                    )}
                    <CardContent sx={{ flex: 1 }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                        {post.categories?.map((c) => (
                          <Chip key={c.id} label={c.name} size="small" variant="outlined" color="primary" />
                        ))}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>{post.title}</Typography>
                      {post.excerpt && (
                        <Typography variant="body2" color="text.secondary" sx={{
                          display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden"
                        }}>
                          {post.excerpt}
                        </Typography>
                      )}
                    </CardContent>
                    {post.published_at && (
                      <Box px={2} pb={2}>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(post.published_at), "MMM d, yyyy")}
                        </Typography>
                      </Box>
                    )}
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <WebsiteFooter settings={settings} school={school} variant="modern" />
    </Box>
  );
}
